/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

import { getPricing, type PricingResponse } from "@/lib/api";
import { formatPrice } from "@/lib/pricing";

export const DEFAULT_PRICING: PricingResponse = {
  paid: {
    original_price_cents: 4900,
    offer_price_cents: null,
    effective_price_cents: 4900,
    offer_label: null,
    has_offer: false,
  },
};

export interface PricingCopy {
  pricing: PricingResponse;
  paidPriceLabel: string;
  paidOriginalPriceLabel: string;
  paidOfferLabel: string | null;
  paidHasOffer: boolean;
  unlockLabel: string;
}

const PricingContext = createContext<PricingCopy>(toPricingCopy(DEFAULT_PRICING));

export function PricingProvider({ children }: { children: ReactNode }) {
  const [pricing, setPricing] = useState<PricingResponse>(DEFAULT_PRICING);

  useEffect(() => {
    let alive = true;
    getPricing()
      .then((nextPricing) => {
        if (alive) {
          setPricing(nextPricing);
        }
      })
      .catch(() => {
        if (alive) {
          setPricing(DEFAULT_PRICING);
        }
      });

    return () => {
      alive = false;
    };
  }, []);

  const value = useMemo(() => toPricingCopy(pricing), [pricing]);
  return <PricingContext.Provider value={value}>{children}</PricingContext.Provider>;
}

export function usePricingCopy(): PricingCopy {
  return useContext(PricingContext);
}

export function toPricingCopy(pricing: PricingResponse): PricingCopy {
  const cents = pricing.paid.has_offer
    ? pricing.paid.effective_price_cents
    : pricing.paid.original_price_cents;
  const paidPriceLabel = formatPrice(cents);
  return {
    pricing,
    paidPriceLabel,
    paidOriginalPriceLabel: formatPrice(pricing.paid.original_price_cents),
    paidOfferLabel: pricing.paid.has_offer ? (pricing.paid.offer_label ?? "Limited Time") : null,
    paidHasOffer: pricing.paid.has_offer,
    unlockLabel: `Unlock the appeal — ${paidPriceLabel}`,
  };
}
